import type { AxiosResponse } from 'axios'
import axios from 'axios'

import type { PaginatedResponse } from '@/api/schemas/common/pagination'
import { API_ENDPOINTS } from '@/constants/apiEndpoints'

export interface CertificateApprovalTrader {
    id: number
    firstName: string
    lastName: string
    email: string
    username: string
}

export interface CertificateApprovalPaperCertificate {
    id: number
    name: string
    code: string
}

export interface CertificateApprovalListItem {
    id: number
    transferredToTechnologistAt: string
    jobNumber: string
    jobName: string
    paperCertificate: CertificateApprovalPaperCertificate | null
    approvalNumber: string
    trader: CertificateApprovalTrader | null
    createdManually: boolean
}

export type CertificateApprovalsListResponse = PaginatedResponse<CertificateApprovalListItem>

export interface CertificateApprovalPaper {
    id: number
    name: string
}

export interface CertificateApprovalPaperPosition {
    id: number
    paper: CertificateApprovalPaper | null
    description: string
    receivedAmount: number | null
    receivedWeight: number | null
    invoiceNumber: string
    supplier: string
    financeDepartmentPaperName: string
}

export interface CertificateApprovalDetail extends CertificateApprovalListItem {
    supplyChainDate: string | null
    paperPositions: CertificateApprovalPaperPosition[]
    paperWasteSentToClient: number | null
    losses: number | null
    netWeightSentToClient: number | null
    salesInvoiceDate: string | null
    salesInvoiceNumber: string
    productName: string
}

export interface CertificateApprovalPaperPositionPayload extends Omit<CertificateApprovalPaperPosition, 'id' | 'paper'> {
    paperId: number | null
}

export interface UpdateCertificateApprovalPayload {
    approvalNumber: string
    supplyChainDate: string
    paperPositions: CertificateApprovalPaperPositionPayload[]
    paperWasteSentToClient: number | null
    losses: number | null
    netWeightSentToClient: number | null
    salesInvoiceDate: string | null
    salesInvoiceNumber: string
    productName: string
    transferredToTechnologistAt?: string
    jobNumber?: string
    jobName?: string
    paperCertificateId?: number
    traderId?: number
}

export interface CreateCertificateApprovalPayload extends UpdateCertificateApprovalPayload {
    transferredToTechnologistAt: string
    jobNumber: string
    jobName: string
    paperCertificateId: number
    traderId: number
    paperWasteSentToClient: number
}

class CertificateApprovalsQueries {
    fetchCertificateApprovals(queryString = ''): Promise<AxiosResponse<CertificateApprovalsListResponse>> {
        const url = queryString
            ? `${API_ENDPOINTS.CERTIFICATE_APPROVALS.LIST}?${queryString}`
            : API_ENDPOINTS.CERTIFICATE_APPROVALS.LIST

        return axios.get<CertificateApprovalsListResponse>(url)
    }

    fetchCertificateApproval(approvalId: number | string): Promise<AxiosResponse<CertificateApprovalDetail>> {
        return axios.get<CertificateApprovalDetail>(API_ENDPOINTS.CERTIFICATE_APPROVALS.DETAIL(approvalId))
    }

    updateCertificateApproval(
        approvalId: number | string,
        payload: UpdateCertificateApprovalPayload
    ): Promise<AxiosResponse<CertificateApprovalDetail>> {
        return axios.patch<CertificateApprovalDetail>(API_ENDPOINTS.CERTIFICATE_APPROVALS.DETAIL(approvalId), payload)
    }

    createCertificateApproval(payload: CreateCertificateApprovalPayload): Promise<AxiosResponse<CertificateApprovalDetail>> {
        return axios.post<CertificateApprovalDetail>(API_ENDPOINTS.CERTIFICATE_APPROVALS.LIST, payload)
    }

    deleteCertificateApproval(approvalId: number | string): Promise<AxiosResponse<void>> {
        return axios.delete(API_ENDPOINTS.CERTIFICATE_APPROVALS.DETAIL(approvalId))
    }
}

export default new CertificateApprovalsQueries()
